import React, { useCallback, useEffect, useState } from "react";
import { App, Form, Input, InputNumber, Modal, Select } from "antd";
import { EditOutlined, PlusOutlined } from "@ant-design/icons";
import { saveProduct } from "../api";
import { CLOTHING_CATEGORY } from "../constants";
import { inventoryToVariants, variantsToInventory } from "../utils";
import ClothingFields from "./ClothingFields";
import InventoryVariantsTable from "./InventoryVariantsTable";
import ImageUploadField from "./ImageUploadField";

const INITIAL_VALUES = { is_colective: 0 };

export default function ProductFormModal({
  open,
  product, // null => crear, objeto => editar
  categories,
  onClose,
  onSaved,
}) {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState([]);
  const [inventory, setInventory] = useState({});
  const [saving, setSaving] = useState(false);

  const isEditing = Boolean(product);
  const category = Form.useWatch("category", form);
  const gender = Form.useWatch("gender", form);
  const sizes = Form.useWatch("sizes", form) || [];
  const colors = Form.useWatch("colors", form) || [];
  const isClothing = category === CLOTHING_CATEGORY;
  const hasImage = fileList.length > 0 || Boolean(product?.image);

  // Cada vez que se abre, se reinicia o se precarga el formulario
  useEffect(() => {
    if (!open) return;
    form.resetFields();
    setFileList([]);
    setInventory({});

    if (!product) return;

    const variants = product.variants || [];
    // El formulario solo permite un sexo a la vez; si hubiera variantes con
    // más de uno (cargadas por otra vía), se precarga el primero.
    const gender = variants[0]?.gender;
    const sizes = [...new Set(variants.map((v) => v.size))];
    const colors = [...new Set(variants.map((v) => v.color))];

    form.setFieldsValue({
      ...product,
      is_colective: Number(product.is_colective) || 0,
      ...(gender ? { gender, sizes, colors } : {}),
    });
    setInventory(variantsToInventory(variants));
  }, [open, product, form]);

  const handleInventoryChange = useCallback(
    (key, value) => setInventory((prev) => ({ ...prev, [key]: value })),
    []
  );

  const handleSubmit = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    if (!hasImage) {
      message.error("Debe seleccionar una imagen");
      return;
    }

    const variants = isClothing
      ? inventoryToVariants(inventory, gender ? [gender] : [], sizes, colors)
      : undefined;

    setSaving(true);
    try {
      await saveProduct({
        values,
        imageFile: fileList[0]?.originFileObj,
        id: product?.id,
        variants,
      });
      message.success(isEditing ? "Producto actualizado" : "Producto creado");
      onSaved();
    } catch (error) {
      console.error("Error al guardar el producto:", error);
      message.error("Error al guardar el producto");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={
        <span>
          {isEditing ? <EditOutlined /> : <PlusOutlined />}{" "}
          {isEditing ? "Editar Producto" : "Crear Producto"}
        </span>
      }
      open={open}
      onCancel={onClose}
      onOk={handleSubmit}
      confirmLoading={saving}
      okText="Guardar"
      cancelText="Cancelar"
      forceRender
    >
      <Form form={form} layout="vertical" initialValues={INITIAL_VALUES}>
        <Form.Item name="name" label="Nombre" rules={[{ required: true }]}>
          <Input />
        </Form.Item>

        <Form.Item
          name="description"
          label="Descripción"
          rules={[{ required: true }]}
        >
          <Input.TextArea />
        </Form.Item>

        <Form.Item
          name="category"
          label="Categoría"
          rules={[{ required: true }]}
        >
          <Select
            placeholder="Selecciona una categoría"
            options={categories.map((c) => ({
              value: c.category,
              label: c.category,
            }))}
          />
        </Form.Item>

        {isClothing && <ClothingFields />}

        {isClothing ? (
          <InventoryVariantsTable
            genders={gender ? [gender] : []}
            sizes={sizes}
            colors={colors}
            inventory={inventory}
            onChange={handleInventoryChange}
          />
        ) : (
          <>
            <Form.Item
              name="stock"
              label="Inventario"
              rules={[{ required: true }]}
            >
              <InputNumber min={0} style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item
              name="max_stock"
              label="Inventario Máximo"
              rules={[{ required: true }]}
            >
              <InputNumber min={0} style={{ width: "100%" }} />
            </Form.Item>
          </>
        )}

        <Form.Item
          name="price"
          label="Precio en Bravos"
          rules={[{ required: true }]}
        >
          <InputNumber min={1} style={{ width: "100%" }} />
        </Form.Item>

        <Form.Item name="cash" label="Precio en Efectivo (MXN)">
          <InputNumber min={1} style={{ width: "100%" }} />
        </Form.Item>

        <Form.Item name="is_colective" label="¿Es un producto colectivo?">
          <Select
            options={[
              { value: 1, label: "Sí" },
              { value: 0, label: "No" },
            ]}
          />
        </Form.Item>

        <ImageUploadField
          fileList={fileList}
          onChange={setFileList}
          hasImage={hasImage}
        />
      </Form>
    </Modal>
  );
}
